import type { FeatureGate } from '@infra/licence/FeatureGate';

export interface ModuleDefinition {
  code: string;
  label: string;
  emoji: string;
  views: string[];
}

/** Catalogue commun aux droits signés et aux 22 modules fonctionnels certifiés. */
export const MODULE_DEFINITIONS: ModuleDefinition[] = [
  {
    code: 'personnel',
    label: 'Administration du personnel',
    emoji: '👥',
    views: [
      'entreprise',
      'salaries',
      'coffrefort',
      'attestations',
      'evaluations',
      'recrutement',
      'flotte',
      'frais',
      'self-docs',
    ],
  },
  { code: 'conges', label: 'Congés et absences', emoji: '📅', views: ['conges', 'self-conges'] },
  {
    code: 'paie',
    label: 'Calcul de la paie',
    emoji: '💰',
    views: ['saisie', 'bulletins', 'self-bulletins'],
  },
  { code: 'cotisations', label: 'Cotisations sociales', emoji: '🏛️', views: ['declarations'] },
  { code: 'irpp', label: 'Impôt sur le revenu', emoji: '🧾', views: ['declarations'] },
  {
    code: 'comptabilite',
    label: 'Comptabilité générale',
    emoji: '📒',
    views: ['comptabilite', 'tiers'],
  },
  {
    code: 'paie_comptabilite',
    label: 'Passerelle paie-comptabilité',
    emoji: '🔗',
    views: ['comptabilite'],
  },
  { code: 'achats', label: 'Achats fournisseurs', emoji: '🛒', views: ['achats'] },
  { code: 'ventes', label: 'Ventes clients', emoji: '🧮', views: ['ventes'] },
  { code: 'stocks', label: 'Stocks', emoji: '📦', views: ['stocks'] },
  { code: 'immobilisations', label: 'Immobilisations', emoji: '🏢', views: ['immobilisations'] },
  { code: 'analytique', label: 'Comptabilité analytique', emoji: '📊', views: ['analytique'] },
  { code: 'rapprochement', label: 'Rapprochement bancaire', emoji: '🏦', views: ['comptabilite'] },
  { code: 'devises', label: 'Gestion des devises', emoji: '💱', views: ['parametres'] },
  { code: 'cloture', label: 'Clôture annuelle', emoji: '🔒', views: ['exercices'] },
  { code: 'tva', label: 'Déclarations de TVA', emoji: '🧾', views: ['tva'] },
  {
    code: 'fiscalite',
    label: 'Fiscalité gabonaise',
    emoji: '⚖️',
    views: ['conformite', 'echeances-fiscales'],
  },
  {
    code: 'liasse',
    label: 'Liasse fiscale et états financiers',
    emoji: '📈',
    views: ['conformite', 'etats'],
  },
  { code: 'annexes', label: 'Notes annexes', emoji: '📝', views: ['conformite'] },
  {
    code: 'gouvernance',
    label: 'Gouvernance et vie sociale',
    emoji: '🤝',
    views: ['vie-sociale', 'sanctions'],
  },
  { code: 'rgpd', label: 'Protection des données et audit', emoji: '🛡️', views: ['rgpd', 'confidentialite'] },
  { code: 'qualite', label: 'Contrôles qualité', emoji: '✅', views: ['qualite'] },
];

/** Écrans toujours disponibles quel que soit le plan (pilotage minimal). */
export const ALWAYS_VISIBLE_VIEWS = new Set([
  /** « Mon entreprise » (raison sociale, NIF) : nécessaire quelle que soit l'offre achetée. */
  'entreprise',
  'dashboard',
  'pilotage',
  'modules',
  'nexxus-factures',
  'parametres',
  'confidentialite',
  'audit',
]);

/** Vues réservées aux licences Cabinet (multi-entités) signées côté admin. */
export const CABINET_ONLY_VIEWS = new Set(['cabinet', 'portefeuille']);

export function viewsForFeatureGate(gate: FeatureGate): Set<string> {
  const allowed = new Set(ALWAYS_VISIBLE_VIEWS);
  for (const module of MODULE_DEFINITIONS) {
    if (gate.has(module.code)) {
      for (const view of module.views) allowed.add(view);
    }
  }
  return allowed;
}

export function isViewAllowedForFeatureGate(view: string, gate: FeatureGate): boolean {
  return viewsForFeatureGate(gate).has(view);
}

export function moduleLabel(code: string): string {
  return MODULE_DEFINITIONS.find((module) => module.code === code)?.label ?? code;
}
